import React from 'react';
import styles from '../styles/DrawVerificationModal.module.css';

interface DrawVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  draw: {
    date: string;
    seed?: string;
    seedHash: string;
    participantCount: number;
    winnerSeqNumber?: number;
  };
}

const DrawVerificationModal: React.FC<DrawVerificationModalProps> = ({
  isOpen,
  onClose,
  draw
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log(`${label} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const calculateWinnerFromSeed = (seed: string, participantCount: number): number => {
    // Convert seed to number and mod by participant count (same logic as backend)
    const seedBigInt = BigInt('0x' + seed);
    return Number(seedBigInt % BigInt(participantCount));
  };

  const verifyWinner = () => {
    if (!draw.seed) return null;
    const calculatedWinner = calculateWinnerFromSeed(draw.seed, draw.participantCount);
    return calculatedWinner === draw.winnerSeqNumber;
  };

  return (
    <div className={styles.drawVerificationOverlay}>
      <div className={styles.drawVerificationModal}>
        <div className={styles.verificationHeader}>
          <h2>🔍 Draw Verification</h2>
          <button onClick={onClose} className={styles.verificationClose} type="button">
            ×
          </button>
        </div>

        <div className={styles.verificationContent}>
          <div className={styles.drawInfoSection}>
            <h3>Draw Information</h3>
            <div className={styles.infoRow}>
              <span className={styles.label}>Date:</span>
              <span className={styles.value}>{formatDate(draw.date)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Participants:</span>
              <span className={styles.value}>{draw.participantCount}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Winner Seq:</span>
              <span className={styles.value}>#{draw.winnerSeqNumber}</span>
            </div>
          </div>

          <div className={styles.verificationSection}>
            <h3>Provably Fair Verification</h3>
            
            <div className={styles.verificationStep}>
              <h4>1. Pre-committed Seed Hash</h4>
              <p>This hash was generated and stored BEFORE the draw began, proving the seed was predetermined:</p>
              <div className={styles.hashContainer}>
                <code className={styles.hashValue}>{draw.seedHash}</code>
                <button 
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(draw.seedHash, 'Seed Hash')}
                  title="Copy seed hash"
                >
                  📋
                </button>
              </div>
            </div>

            {draw.seed && (
              <>
                <div className={styles.verificationStep}>
                  <h4>2. Revealed Seed</h4>
                  <p>After the draw completed, the original seed was revealed:</p>
                  <div className={styles.hashContainer}>
                    <code className={styles.hashValue}>{draw.seed}</code>
                    <button 
                      className={styles.copyButton}
                      onClick={() => draw.seed && copyToClipboard(draw.seed, 'Seed')}
                      title="Copy seed"
                    >
                      📋
                    </button>
                  </div>
                </div>

                <div className={styles.verificationStep}>
                  <h4>3. Winner Calculation</h4>
                  <p>The winner is determined by this formula:</p>
                  <div className={styles.formula}>
                    <code>Winner Seq Number = Seed % Participant Count</code>
                  </div>
                  <div className={styles.calculation}>
                    <code>
                      Winner = {draw.seed.slice(0, 16)}...{draw.seed.slice(-8)} % {draw.participantCount} = #{calculateWinnerFromSeed(draw.seed, draw.participantCount)}
                    </code>
                  </div>
                  
                  {verifyWinner() ? (
                    <div className={`${styles.verificationResult} ${styles.success}`}>
                      ✅ Verification Successful - Winner calculation matches!
                    </div>
                  ) : (
                    <div className={`${styles.verificationResult} ${styles.error}`}>
                      ❌ Verification Failed - Winner calculation doesn't match
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className={styles.explanationSection}>
            <h3>How It Works</h3>
            <ul>
              <li><strong>Pre-commitment:</strong> Before any draw begins, a random seed is generated and its hash is stored publicly</li>
              <li><strong>Transparency:</strong> The hash proves the seed existed before participants joined</li>
              <li><strong>Fairness:</strong> After the draw, the original seed is revealed for verification</li>
              <li><strong>Deterministic:</strong> Anyone can verify the winner using the revealed seed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawVerificationModal;